import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Shield, ChevronRight, ChevronLeft, CheckCircle2, AlertTriangle, Clock, Play, Download, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_STYLES = { GREEN: 'bg-[#4CAF50] text-white', AMBER: 'bg-[#FF9800] text-white', RED: 'bg-[#F44336] text-white', GREY: 'bg-[#9E9E9E] text-white' };
const STEP_STATUS = { met: 'bg-[#4CAF50]/10 border-[#4CAF50]/30 text-[#4CAF50]', partial: 'bg-[#FF9800]/10 border-[#FF9800]/30 text-[#FF9800]', not_met: 'bg-[#F44336]/10 border-[#F44336]/30 text-[#F44336]' };

export default function DSPTAssessment() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [step, setStep] = useState(0);
  const [notes, setNotes] = useState('');

  useEffect(() => { fetchAssessment(); }, []);

  const fetchAssessment = async () => {
    try { const { data: d } = await axios.get(`${API}/api/dspt/assessment`, { withCredentials: true }); setData(d); setStep(d.assessment?.current_step || 0); } catch { /* ignore */ }
  };

  const startAssessment = async () => {
    try { await axios.post(`${API}/api/dspt/assessment/start`, {}, { withCredentials: true }); toast.success('Assessment started'); fetchAssessment(); } catch { toast.error('Failed'); }
  };

  const confirmAssertion = async (assertionId, confirmed) => {
    try {
      await axios.put(`${API}/api/dspt/assessment/respond`, { assertion_id: assertionId, confirmed, notes, step: step + 1 }, { withCredentials: true });
      toast.success(confirmed ? 'Assertion confirmed' : 'Response saved');
      setNotes('');
      if (step < (data?.assertions?.length || 10) - 1) setStep(step + 1);
      fetchAssessment();
    } catch { toast.error('Failed'); }
  };

  const completeAssessment = async () => {
    try { await axios.post(`${API}/api/dspt/assessment/complete`, {}, { withCredentials: true }); toast.success('DSPT Assessment completed'); fetchAssessment(); } catch { toast.error('Failed'); }
  };

  const exportPack = async () => {
    try {
      const resp = await axios.get(`${API}/api/dspt/export-pack`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'DSPT_Evidence_Pack.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('Evidence pack downloaded');
    } catch { toast.error('Export failed'); }
  };

  if (!data) return <DashboardLayout><div className="flex justify-center py-20"><div className="spinner" /></div></DashboardLayout>;

  const assertions = data.assertions || [];
  const assessment = data.assessment || {};
  const currentA = assertions[step];
  const isActive = assessment.status === 'in_progress';
  const isComplete = assessment.status === 'completed';
  const confirmedCount = assertions.filter(a => a.confirmed).length;

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="dspt-assessment-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#00A6A0]" />DSPT Annual Assessment
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">Guided walkthrough of all 10 DSPT assertions</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-border text-xs" onClick={exportPack} data-testid="export-pack-btn">
              <Download className="w-3 h-3 mr-1" />Evidence Pack
            </Button>
          </div>
        </div>

        {/* Progress */}
        <Card className="glass-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Overall: {data.overall_compliance}% ({data.total_green}/{data.total_controls} controls GREEN)</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${isComplete ? 'bg-[#4CAF50]/10 text-[#4CAF50]' : isActive ? 'bg-[#FF9800]/10 text-[#FF9800]' : 'bg-muted text-muted-foreground'}`}>
                {isComplete ? 'Completed' : isActive ? `In Progress (${confirmedCount}/${assertions.length})` : 'Not Started'}
              </span>
            </div>
            <div className="flex gap-1">
              {assertions.map((a, i) => (
                <button key={a.id} onClick={() => setStep(i)} className={`flex-1 h-2 rounded-full transition-colors ${i === step ? 'ring-2 ring-[#00A6A0]' : ''} ${a.confirmed ? 'bg-[#4CAF50]' : a.status === 'met' ? 'bg-[#4CAF50]/40' : a.status === 'partial' ? 'bg-[#FF9800]/40' : 'bg-[#F44336]/40'}`}
                  title={`${a.id}. ${a.title} — ${a.status}`} data-testid={`step-${a.id}`} />
              ))}
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
              <span>1. Leadership</span><span>10. Incidents</span>
            </div>
          </CardContent>
        </Card>

        {/* Start button */}
        {!isActive && !isComplete && (
          <Card className="glass-card border-border">
            <CardContent className="p-6 text-center">
              <Shield className="w-12 h-12 text-[#00A6A0] mx-auto mb-3" />
              <h2 className="text-lg font-medium text-foreground mb-1">Begin DSPT Annual Assessment</h2>
              <p className="text-sm text-muted-foreground mb-4">Walk through each of the 10 DSPT assertions, review evidence, and confirm compliance.</p>
              <Button className="btn-primary" onClick={startAssessment} data-testid="start-assessment-btn">
                <Play className="w-4 h-4 mr-1" />Start Assessment
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Wizard Step */}
        {(isActive || isComplete) && currentA && (
          <div className="space-y-4">
            <Card className={`border ${STEP_STATUS[currentA.status] || 'border-border'}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg font-bold ${currentA.status === 'met' ? 'bg-[#4CAF50]/10 text-[#4CAF50]' : currentA.status === 'partial' ? 'bg-[#FF9800]/10 text-[#FF9800]' : 'bg-[#F44336]/10 text-[#F44336]'}`}>
                    {currentA.id}
                  </div>
                  <div>
                    <h2 className="text-lg font-medium text-foreground">{currentA.title}</h2>
                    <p className="text-sm text-muted-foreground">{currentA.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${STEP_STATUS[currentA.status]}`}>{currentA.greens}/{currentA.total} controls met</span>
                      {currentA.confirmed && <span className="text-xs px-2 py-0.5 rounded-full bg-[#4CAF50]/10 text-[#4CAF50] border border-[#4CAF50]/30">Confirmed</span>}
                    </div>
                  </div>
                </div>

                {/* Controls */}
                <div className="space-y-2 mb-4">
                  {(currentA.controls || []).map(c => (
                    <div key={c.id} className="flex items-start gap-2 p-2 rounded-lg bg-muted/30 text-xs">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold flex-shrink-0 ${STATUS_STYLES[c.status]}`}>{c.status}</span>
                      <div className="flex-1">
                        <p className="text-foreground font-medium">[{c.id}] {c.title}</p>
                        <p className="text-muted-foreground mt-0.5">{c.evidence}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Admin response */}
                {isActive && (
                  <div className="border-t border-border pt-3 space-y-2">
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Admin notes for this assertion (optional)..." className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px]" />
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" className="border-border" onClick={() => confirmAssertion(currentA.id, false)} data-testid="save-notes-btn">Save Notes</Button>
                      <Button size="sm" className="bg-[#4CAF50] hover:bg-[#45A049] text-white" onClick={() => confirmAssertion(currentA.id, true)} data-testid="confirm-assertion-btn">
                        <CheckCircle2 className="w-3 h-3 mr-1" />Confirm Assertion
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button size="sm" variant="outline" className="border-border" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} data-testid="prev-step-btn">
                <ChevronLeft className="w-4 h-4 mr-1" />Previous
              </Button>
              <span className="text-xs text-muted-foreground">Step {step + 1} of {assertions.length}</span>
              {step < assertions.length - 1 ? (
                <Button size="sm" variant="outline" className="border-border" onClick={() => setStep(step + 1)} data-testid="next-step-btn">
                  Next<ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : isActive ? (
                <Button size="sm" className="btn-primary" onClick={completeAssessment} data-testid="complete-assessment-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1" />Complete Assessment
                </Button>
              ) : (
                <span className="text-xs text-[#4CAF50]">Assessment Complete</span>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
